"use client";

import { UploadIcon } from "lucide-react";
import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import type { DropEvent, DropzoneOptions, FileRejection } from "react-dropzone";
import { useDropzone } from "react-dropzone";
import { cn } from "@/utils";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { i18n } from "@lingui/core";

type DropzoneContextType = {
  src?: File[];
  accept?: DropzoneOptions["accept"];
  maxSize?: DropzoneOptions["maxSize"];
  minSize?: DropzoneOptions["minSize"];
  maxFiles?: DropzoneOptions["maxFiles"];
};

const renderBytes = (bytes: number) => {
  const units = ["B", "KB", "MB", "GB", "TB", "PB"];
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex++;
  }

  return `${size.toFixed(2)}${units[unitIndex]}`;
};

const DropzoneContext = createContext<DropzoneContextType | undefined>(
  undefined,
);

export type DropzoneProps = Omit<DropzoneOptions, "onDrop"> & {
  src?: File[];
  className?: string;
  onDrop?: (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    event: DropEvent,
  ) => void;
  children?: ReactNode;
};

export const Dropzone = ({
  accept,
  maxFiles = 1,
  maxSize,
  minSize,
  onDrop,
  onError,
  disabled,
  src,
  className,
  children,
  ...props
}: DropzoneProps) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept,
    maxFiles,
    maxSize,
    minSize,
    onError,
    disabled,
    onDrop: (acceptedFiles, fileRejections, event) => {
      if (fileRejections.length > 0) {
        const message = fileRejections.at(0)?.errors.at(0)?.message;
        onError?.(new Error(message));
        return;
      }

      onDrop?.(acceptedFiles, fileRejections, event);
    },
    ...props,
  });

  return (
    <DropzoneContext.Provider
      key={JSON.stringify(src)}
      value={{ src, accept, maxSize, minSize, maxFiles }}
    >
      <div
        {...getRootProps({
          "aria-label": t`Povlečite in spustite ali kliknite za nalaganje`,
          className: cn(
            "relative flex h-auto w-full flex-col overflow-hidden rounded-lg border border-default-200 bg-default-100 p-4 transition-colors hover:bg-default-200 cursor-pointer select-none",
            isDragActive && "ring-ring ring-1 outline-none",
            disabled && "pointer-events-none opacity-50",
            className,
          ),
          role: "button",
        })}
      >
        <input {...getInputProps()} disabled={disabled} />
        {children}
      </div>
    </DropzoneContext.Provider>
  );
};

const useDropzoneContext = () => {
  const context = useContext(DropzoneContext);

  if (!context) {
    throw new Error("useDropzoneContext must be used within a Dropzone");
  }

  return context;
};

export type DropzoneContentProps = {
  children?: ReactNode;
  className?: string;
};

const maxLabelItems = 3;
const maxFileNameLength = 25;

const truncateFileName = (name: string, maxLength = maxFileNameLength) => {
  if (name.length <= maxLength) return name;
  const keepLength = maxLength - 3;
  const startChars = Math.ceil(keepLength / 2);
  const endChars = Math.floor(keepLength / 2);
  return `${name.slice(0, startChars)}...${name.slice(-endChars)}`;
};

export const DropzoneContent = ({
  children,
  className,
}: DropzoneContentProps) => {
  const { src } = useDropzoneContext();

  if (!src) {
    return null;
  }

  if (children) {
    return children;
  }

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-center gap-4",
        className,
      )}
    >
      <div className="bg-muted text-muted-foreground flex size-8 shrink-0 items-center justify-center rounded-md">
        <UploadIcon size={16} />
      </div>
      <div className="flex min-w-0 flex-col">
        <p className="w-full truncate text-left text-sm font-medium">
          {src.length > maxLabelItems
            ? t`${new Intl.ListFormat(i18n.locale).format(
                src
                  .slice(0, maxLabelItems)
                  .map((file) => truncateFileName(file.name)),
              )} in ${src.length - maxLabelItems} več`
            : new Intl.ListFormat(i18n.locale).format(
                src.map((file) => truncateFileName(file.name)),
              )}
          {" · "}
          {renderBytes(src.reduce((sum, file) => sum + file.size, 0))}
        </p>
        <p className="text-muted-foreground w-full truncate text-left text-xs text-wrap">
          <Trans>Povlečite in spustite ali kliknite za zamenjavo</Trans>
        </p>
      </div>
    </div>
  );
};

export type DropzoneEmptyStateProps = {
  children?: ReactNode;
  className?: string;
};

export const DropzoneEmptyState = ({
  children,
  className,
}: DropzoneEmptyStateProps) => {
  const { src, accept, maxSize, minSize, maxFiles } = useDropzoneContext();

  if (src) {
    return null;
  }

  if (children) {
    return children;
  }

  let caption = "";

  if (accept) {
    caption += t`Sprejme `;
    caption += new Intl.ListFormat(i18n.locale).format(Object.keys(accept));
  }

  if (minSize && maxSize) {
    caption += t` med ${renderBytes(minSize)} in ${renderBytes(maxSize)}`;
  } else if (minSize) {
    caption += t` vsaj ${renderBytes(minSize)}`;
  } else if (maxSize) {
    caption += t` manj kot ${renderBytes(maxSize)}`;
  }

  return (
    <div
      className={cn(
        "flex flex-row items-center justify-center gap-4",
        className,
      )}
    >
      <div className="bg-muted text-muted-foreground flex size-8 items-center justify-center rounded-md">
        <UploadIcon size={16} />
      </div>
      <div className="flex flex-col">
        <p className="w-full truncate text-left text-sm font-medium text-wrap">
          <Trans>Naloži {maxFiles === 1 ? t`datoteko` : t`datoteke`}</Trans>
        </p>
        <p className="text-muted-foreground w-full truncate text-left text-xs text-wrap">
          <Trans>Povlečite in spustite ali kliknite za nalaganje</Trans>
        </p>
        {caption && (
          <p className="text-muted-foreground text-xs text-wrap">{caption}.</p>
        )}
      </div>
    </div>
  );
};
